import React from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import { ArrowDropDownRounded, LockOutlineRounded } from "@mui/icons-material";
import SteamSignInButton from "../../auth/SteamSignInButton";
import PlaySafeSignInButton from "../../auth/PlaySafeSignInButton";
import { useAuthState } from "../../app/AuthStateContext";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import { AnimatePresence, motion } from "framer-motion";
import { colors } from "./colors";
import HeroBenefits from "./HeroBenefits";
import OuroCompared from "./OuroCompared";
import HowOuroWorks from "./HowOuroWorks";
import MatchNightSection from "./MatchNightSection";
import ReputationSystem from "./ReputationSystem";
import ServerMapSection from "./ServersMapSection";
import OuroTiers from "./OuroTiers";
import { ULTRA_WIDE_MEDIA } from "./breakpoints";
import { CENTERED_GLOW_SX, OVERFLOW_X_CLIP_SX } from "./layoutGuards";
const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HERO_BG = "#080808";

const HERO_BACKGROUND_URL =
  "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/backgrounds/public-site-ouro-hero-background.jpeg";

const NOISE_TEXTURE = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(#n)"/></svg>'
)}")`;

const HERO_FLOAT_KEYFRAMES = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -51%) scale(1.025);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const HERO_PHOTO_ROOT_SX = {
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: 0,
  backgroundColor: HERO_BG,
};

function HeroPhotoBackground() {
  return (
    <Box aria-hidden sx={HERO_PHOTO_ROOT_SX}>
      <MotionBox
        component="img"
        initial={{
          scale: 1.12,
          opacity: 0,
          filter: "blur(10px) saturate(1.15) contrast(1.08)",
        }}
        animate={{
          scale: 1,
          opacity: 1,
          filter: "blur(0px) saturate(1.06) contrast(1.04)",
        }}
        transition={{
          duration: 1.8,
          ease: [0.22, 1, 0.36, 1],
        }}
        src={HERO_BACKGROUND_URL}
        alt=""
        loading="eager"
        decoding="async"
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) scale(1.08)",
          objectFit: "cover",
          objectPosition: { xs: "70% 40%", md: "62% 38%" },
          filter: "saturate(1.06) contrast(1.04)",
          animation: `${HERO_FLOAT_KEYFRAMES} 18s ease-in-out infinite`,
          willChange: "transform",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(
              102deg,
              rgba(5,5,5,0.96) 0%,
              rgba(5,5,5,0.88) 18%,
              rgba(5,5,5,0.75) 38%,
              rgba(5,5,5,0.38) 52%,
              rgba(5,5,5,0.08) 64%,
              transparent 78%
            )
          `,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(
              ellipse 85% 75% at 68% 40%,
              rgba(232,163,61,0.1) 0%,
              transparent 58%
            )
          `,
          mixBlendMode: "screen",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: NOISE_TEXTURE,
          backgroundRepeat: "repeat",
          backgroundSize: "180px 180px",
          opacity: 0.03,
          mixBlendMode: "soft-light",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          boxShadow: `
            inset 0 0 100px rgba(0,0,0,0.45),
            inset 0 0 220px rgba(0,0,0,0.7),
            inset 0 -60px 80px rgba(0,0,0,0.35)
          `,
        }}
      />
    </Box>
  );
}

const HERO_CONTENT_SX = {
  position: "relative",
  zIndex: 5,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  ...OVERFLOW_X_CLIP_SX,
  mx: "auto",
  px: { xs: 2, sm: 2.5, md: 3 },
  py: { xs: 3, md: 4 },
  textAlign: "center",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    pt: "clamp(5.5rem, 7vh, 7rem)",
    pb: "clamp(3.25rem, 5vh, 5rem)",
  },
};

const HERO_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const HERO_COPY_ENTER = {
  initial: {
    y: 26,
    opacity: 0,
    filter: "blur(10px)",
  },
  animate: {
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
  },
};

const HERO_COPY_ENTER_TRANSITION = {
  duration: 0.9,
  delay: 0.45,
  ease: [0.22, 1, 0.36, 1],
};

const HERO_HEADLINE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.075em",
  textTransform: "uppercase",
  lineHeight: 1,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "5.25rem",
  },
};

const HERO_EYEBROW_SX = {
  fontSize: { xs: "0.7rem", md: "0.75rem" },
  fontWeight: 600,
  letterSpacing: "0.12em",
  color: colors.primary.hover,
  textShadow: "0 1px 0 rgba(0,0,0,0.45), 0 0 16px rgba(232,163,61,0.22)",
  textTransform: "uppercase",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "0.95rem",
  },
};

const HERO_BODY_SX = {
  fontSize: { xs: "0.875rem", md: "1rem" },
  fontWeight: 500,
  letterSpacing: "0.01em",
  lineHeight: 1.65,
  color: "rgba(255,255,255,0.72)",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "1.15rem",
  },
};

const HERO_BUTTON_ULTRA_SX = {
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    height: "4.5rem",
    minHeight: "4.5rem",
    px: "1.5rem",
    borderRadius: "16px",
    fontSize: "1rem",
    letterSpacing: "0.035em",
    whiteSpace: "nowrap",
    "& .MuiButton-startIcon svg": {
      fontSize: "1.25rem",
    },
    "& svg": {
      maxHeight: "2.5rem",
      height: "2.5rem",
      width: "auto",
    },
  },
};

const HERO_COPY_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "visible",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    maxWidth: "52rem",
  },
};

const HERO_CTA_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  gap: "0.5rem",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  overflow: "visible",
  mt: { xs: "4.25rem", md: "4.75rem" },
};

const HERO_GOOGLE_WRAP_SX = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  pt: 0.75,
  colorScheme: "light",
};

const HERO_SSO_PANEL_SX = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
};

const PLAY_NOW_BUTTON_SX = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  height: { xs: "2.75rem", md: "3.35rem" },
  minHeight: { xs: "2.75rem", md: "3.35rem" },
  px: { xs: "0.65rem", md: "0.9rem" },
  borderRadius: { xs: "12px", md: "14px" },
  border: "1px solid rgba(255,210,120,0.24)",
  background: `
    linear-gradient(
      180deg,
      #f7cb79 0%,
      #f0b458 42%,
      #de8f2c 100%
    )
  `,
  color: "#120c04",
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
  letterSpacing: "0.02em",
  fontFamily: "'Inter', system-ui, sans-serif",
  backdropFilter: "blur(10px)",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.34),
    inset 0 -6px 12px rgba(120,60,0,0.18),
    0 10px 30px rgba(232,163,61,0.24),
    0 0 0 1px rgba(255,190,90,0.05)
  `,
  transition: `
    transform 260ms cubic-bezier(0.22,1,0.36,1),
    box-shadow 260ms cubic-bezier(0.22,1,0.36,1),
    filter 260ms cubic-bezier(0.22,1,0.36,1),
    border-color 260ms ease
  `,
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
  },
  "& .MuiButton-startIcon svg": {
    fontSize: { xs: "0.9rem", md: "1.15rem" },
  },
  "& .MuiButton-startIcon svg path": {
    fill: "currentColor",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-140%",
    width: "58%",
    height: "100%",
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.38),
        transparent
      )
    `,
    transform: "skewX(-24deg)",
    transition: "left 900ms cubic-bezier(0.22,1,0.36,1)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    background: `
      radial-gradient(
        circle at top,
        rgba(255,255,255,0.22),
        transparent 72%
      )
    `,
    opacity: 0.7,
    pointerEvents: "none",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,220,150,0.4)",
    boxShadow: `
      inset 0 1px 0 rgba(255,255,255,0.42),
      inset 0 -10px 18px rgba(120,60,0,0.28),
      0 18px 42px rgba(232,163,61,0.34),
      0 0 26px rgba(246,185,76,0.16)
    `,
    filter: "brightness(1.04) saturate(1.04)",
    "&::before": {
      left: "155%",
    },
  },
  "&:active": {
    transform: "scale(0.985)",
  },
  ...HERO_BUTTON_ULTRA_SX,
};

const HERO_SSO_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: "stretch",
  justifyContent: "center",
  gap: { xs: "0.65rem", md: "1rem" },
  width: "100%",
  minWidth: 0,
  mx: { md: "auto" },
  boxSizing: "border-box",
  maxWidth: { xs: "18rem", md: "none" },
  width: "fit-content",
  mx: "auto",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    gap: "1.25rem",
    maxWidth: "none",
  },
};

const HERO_SSO_COL_SX = {
  flex: { xs: "1 1 auto", md: "0 1 auto" },
  width: { xs: "100%", md: "auto" },
  maxWidth: { xs: "18rem", md: "14.5rem" },
  mx: { xs: "auto", md: 0 },
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    flex: "0 0 19rem",
    width: "19rem",
    maxWidth: "19rem",
  },
};

const HERO_PLAYSAFE_BUTTON_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  height: { xs: "2.75rem", md: "3.35rem" },
  minHeight: { xs: "2.75rem", md: "3.35rem" },
  px: { xs: "0.95rem", md: "1.25rem" },
  borderRadius: { xs: "12px", md: "14px" },
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.28)",
  color: "rgba(255,255,255,0.92)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: `
    transform 260ms cubic-bezier(0.22,1,0.36,1),
    border-color 260ms ease,
    background-color 260ms ease
  `,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "0px",
    background: `
        linear-gradient(
          90deg,
          rgba(0,204,221,0),
          rgba(0,204,221,0.9),
          rgba(0,204,221,0)
        )
      `,
    transition: "height .28s ease",
  },
  "& svg": {
    maxWidth: "100%",
    height: "auto",
    maxHeight: { xs: "1.5rem", md: "2.125rem" },
  },
  "&:hover": {
    transform: "translateY(-2px)",
    backgroundColor: "#09090b",
    borderColor: "transparent",
    boxShadow: `
      inset 0 -1px 0 rgba(0,204,221,.15),
      0 8px 24px rgba(0,204,221,.08)
    `,
    "&::after": {
      height: "2px",
    },
  },
  "&:active": {
    transform: "scale(0.985)",
  },
  ...HERO_BUTTON_ULTRA_SX,
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    ...HERO_BUTTON_ULTRA_SX[`@media ${ULTRA_WIDE_MEDIA}`],
    "& .playsafe-button-inner": {
      gap: "0.85rem",
    },
    "& .playsafe-button-inner span": {
      fontSize: "1rem",
      letterSpacing: "0.035em",
    },
  },
};

const HERO_ASSURANCE_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  mt: "0.35rem",
  color: colors.text.muted,
};

const HERO_ASSURANCE_TEXT_SX = {
  fontSize: "0.75rem",
  color: colors.text.muted,
  lineHeight: 1.4,
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "0.9rem",
  },
};

const HERO_GOOGLE_DIVIDER_SX = {
  width: "100%",
  minWidth: 0,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: { xs: "0.35rem", md: "0.75rem" },
  my: { xs: 1.5, md: 2 },
  boxSizing: "border-box",
};

const HERO_GOOGLE_DIVIDER_LINE_SX = {
  flex: "1 1 0",
  minWidth: 0,
  width: "100%",
  maxWidth: { xs: "2.25rem", sm: "3.5rem", md: "7rem" },
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    maxWidth: "10rem",
  },
  height: "1px",
  background: `
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,0.06) 20%,
      rgba(232,163,61,0.25) 50%,
      rgba(255,255,255,0.06) 80%,
      transparent
    )
  `,
};

const HERO_GOOGLE_DIVIDER_TEXT_SX = {
  fontSize: { xs: "0.625rem", sm: "0.7rem" },
  letterSpacing: { xs: "0.08em", md: "0.14em" },
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "0.85rem",
    letterSpacing: "0.16em",
  },
  textTransform: "uppercase",
  color: "inherit",
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: "nowrap",
};

const HERO_GOOGLE_TOGGLE_SX = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  gap: "0.1rem",
  px: { xs: 0.5, sm: 1, md: 1.5 },
  border: 0,
  background: "transparent",
  cursor: "pointer",
  color: colors.text.secondary,
  whiteSpace: "nowrap",
  transition: "color 200ms ease",
  "&:hover": {
    color: colors.text.primary,
  },
  "&:focus-visible": {
    outline: "2px solid rgba(232,163,61,0.5)",
    outlineOffset: 4,
    borderRadius: "4px",
  },
};

const HERO_GOOGLE_PANEL_INNER_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  pt: 0.75,
  pb: 0.25,
  overflow: "visible",
};

const PAGE_ROOT_SX = {
  ...OVERFLOW_X_CLIP_SX,
  minHeight: "100svh",
  backgroundColor: HERO_BG,
};

const HERO_SECTION_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  ...OVERFLOW_X_CLIP_SX,
  minHeight: "100svh",
  backgroundColor: HERO_BG,
  isolation: "isolate",
};

export default function DarkHeroHomePage({ catalog, templateReady = true }) {
  const { googleResponse, onFailure, isAuthenticated } = useAuthState();
  const { user } = catalog;
  const track = useTemplateTrack();
  const [scrollY, setScrollY] = React.useState(0);
  const [googleSsoOpen, setGoogleSsoOpen] = React.useState(false);
  const isUltraWide = useMediaQuery(ULTRA_WIDE_MEDIA);
  const isHeroMdUp = useMediaQuery("(min-width:900px)");
  const heroGoogleWidth = isUltraWide ? 360 : isHeroMdUp ? 280 : 250;

  React.useEffect(() => {
    let rafId = 0;
    const update = () => {
      rafId = 0;
      setScrollY(window.scrollY || 0);
    };
    const onScroll = () => {
      if (!rafId) rafId = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  React.useEffect(() => {
    if (!templateReady) return;
    track("homepage_view");
  }, [templateReady, track]);

  if (isAuthenticated && user?.email) {
    return <Redirect to="/lobby" />;
  }

  return (
    <Box sx={PAGE_ROOT_SX}>
      <Box sx={HERO_SECTION_SX}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `
            radial-gradient(
              ellipse at center,
              transparent 45%,
              rgba(0,0,0,0.18) 100%
            )
          `,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        <HeroPhotoBackground />

        <MotionBox
          aria-hidden
          animate={{
            opacity: [0.08, 0.14, 0.08],
            scale: [1, 1.04, 1],
          }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            mixBlendMode: "screen",
            background: `
            radial-gradient(
              ellipse 80% 70% at 62% 42%,
              rgba(232,163,61,0.18) 0%,
              rgba(232,163,61,0.08) 42%,
              transparent 72%
            )
          `,
          }}
        />

        <MotionBox
          sx={{
            ...HERO_CONTENT_SX,
          }}
        >
          <Box sx={HERO_STACK_SX}>
            <Box sx={HERO_COPY_SX}>
              <MotionBox
                {...HERO_COPY_ENTER}
                transition={HERO_COPY_ENTER_TRANSITION}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: "1px",
                    background: colors.primary.hover,
                  }}
                />
                <Typography sx={HERO_EYEBROW_SX}>
                  A new era of cs2 matchmaking
                </Typography>
              </MotionBox>
              <MotionTypography
                {...HERO_COPY_ENTER}
                transition={{
                  ...HERO_COPY_ENTER_TRANSITION,
                  delay: 0,
                }}
                sx={{
                  ...HERO_HEADLINE_SX,
                  color: colors.text.primary,
                  textShadow: "0 2px 24px rgba(0,0,0,0.9), 0 1px 4px rgba(0,0,0,1)",
                }}
              >
                Play competitively.
              </MotionTypography>
              <MotionTypography
                {...HERO_COPY_ENTER}
                animate={{
                  ...HERO_COPY_ENTER.animate,
                  backgroundPosition: "100% 50%",
                }}
                initial={{
                  ...HERO_COPY_ENTER.initial,
                  backgroundPosition: "0% 50%",
                }}
                transition={{
                  ...HERO_COPY_ENTER_TRANSITION,
                  duration: 1.1,
                }}
                sx={{
                  position: "relative",
                  ...HERO_HEADLINE_SX,
                  background: `
                  linear-gradient(
                    180deg,
                    #f7cb79 0%,
                    #f0b458 40%,
                    #de8f2c 100%
                  )
                `,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Not pointlessly.
              </MotionTypography>
              <MotionTypography
                {...HERO_COPY_ENTER}
                transition={HERO_COPY_ENTER_TRANSITION}
                sx={HERO_BODY_SX}
              >
                Human-reviewed signups and balanced matches, without the cheaters, the stomps, or the toxicity.
              </MotionTypography>
            </Box>

            <Box sx={HERO_CTA_SX}>
              <MotionBox
                aria-hidden
                animate={{
                  opacity: [0.45, 0.7, 0.45],
                  scale: [1, 1.08, 1],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                sx={{
                  position: "absolute",
                  width: "18rem",
                  height: "18rem",
                  borderRadius: "50%",
                  background: "rgba(232,163,61,0.12)",
                  filter: "blur(90px)",
                  zIndex: -1,
                  top: "-5rem",
                }}
              />
              <MotionBox
                initial={{
                  opacity: 0,
                  y: 18,
                  scale: 0.96,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  delay: 0.9,
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
                sx={HERO_SSO_PANEL_SX}
              >
                <Box sx={HERO_SSO_ROW_SX}>
                  <Box sx={HERO_SSO_COL_SX}>
                    <SteamSignInButton
                      label="Sign in with Steam"
                      logoSize={22}
                      showAssurance={false}
                      surface="hero"
                      fullWidth
                      sx={PLAY_NOW_BUTTON_SX}
                    />
                    <Box sx={HERO_ASSURANCE_SX}>
                      <Typography sx={HERO_ASSURANCE_TEXT_SX}>
                        Read-only sign-in. We never touch your inventory.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={HERO_SSO_COL_SX}>
                    <PlaySafeSignInButton
                      label="Sign in with PlaySafe ID"
                      surface="hero"
                      fullWidth
                      logoHeight={22}
                      sx={HERO_PLAYSAFE_BUTTON_SX}
                    />
                    <Box sx={HERO_ASSURANCE_SX}>
                      <Typography sx={HERO_ASSURANCE_TEXT_SX}>Play instantly</Typography>
                    </Box>
                  </Box>
                </Box>
              </MotionBox>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <MotionBox
                  {...HERO_COPY_ENTER}
                  transition={HERO_COPY_ENTER_TRANSITION}
                  sx={HERO_GOOGLE_DIVIDER_SX}
                >
                  <Box sx={HERO_GOOGLE_DIVIDER_LINE_SX} />
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={googleSsoOpen}
                    aria-controls="hero-google-sso-panel"
                    onClick={() => setGoogleSsoOpen((open) => !open)}
                    sx={HERO_GOOGLE_TOGGLE_SX}
                  >
                    <Typography component="span" sx={HERO_GOOGLE_DIVIDER_TEXT_SX}>
                      or continue with
                    </Typography>
                    <ArrowDropDownRounded
                      aria-hidden
                      sx={{
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        flexShrink: 0,
                        transition: "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)",
                        transform: googleSsoOpen ? "rotate(180deg)" : "rotate(0deg)",
                        [`@media ${ULTRA_WIDE_MEDIA}`]: {
                          fontSize: "1.45rem",
                        },
                      }}
                    />
                  </Box>
                  <Box sx={HERO_GOOGLE_DIVIDER_LINE_SX} />
                </MotionBox>
                <Box
                  id="hero-google-sso-panel"
                  aria-hidden={!googleSsoOpen}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    overflow: "visible",
                    zIndex: 3,
                    pointerEvents: googleSsoOpen ? "auto" : "none",
                  }}
                >
                  <AnimatePresence initial={false}>
                    {googleSsoOpen ? (
                      <MotionBox
                        key="hero-google-sso"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        sx={HERO_GOOGLE_PANEL_INNER_SX}
                      >
                        <Box
                          onClick={() =>
                            track("sso_google_clicked", { provider: "google", surface: "hero" })
                          }
                          sx={HERO_GOOGLE_WRAP_SX}
                        >
                          <GoogleLogin
                            shape="pill"
                            size="large"
                            theme="filled_black"
                            text="continue_with"
                            width={heroGoogleWidth}
                            onSuccess={googleResponse}
                            onError={onFailure}
                          />
                        </Box>
                        <Box sx={HERO_ASSURANCE_SX}>
                          <Typography sx={HERO_ASSURANCE_TEXT_SX}>
                            Play after a 3 to 24h check
                          </Typography>
                        </Box>
                      </MotionBox>
                    ) : null}
                  </AnimatePresence>
                </Box>
              </Box>
            </Box>
          </Box>
        </MotionBox>
        <Box
          sx={{
            position: "absolute",
            bottom: "-1px",
            left: 0,
            width: "100%",
            height: "220px",
            pointerEvents: "none",
            zIndex: 2,

            background: `
              linear-gradient(
                to bottom,
                rgba(8,8,8,0) 0%,
                rgba(8,8,8,0.45) 35%,
                rgba(8,8,8,0.82) 68%,
                #080808 100%
              )
            `,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-120px",
            ...CENTERED_GLOW_SX,
            height: "420px",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 2,

            background: `
              radial-gradient(
                ellipse at center,
                rgba(232,163,61,0.12) 0%,
                rgba(232,163,61,0.06) 32%,
                rgba(232,163,61,0.02) 52%,
                transparent 72%
              )
            `,

            filter: "blur(60px)",
            opacity: 0.7,
          }}
        />
      </Box>
      {/* <HeroBenefits /> */}
      <OuroCompared />
      <HowOuroWorks />
      <MatchNightSection />
      <ReputationSystem />
      <ServerMapSection />
      <OuroTiers />
    </Box>
  );
}

DarkHeroHomePage.propTypes = {
  catalog: PropTypes.shape({
    user: PropTypes.object,
  }),
  templateReady: PropTypes.bool,
};
